import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  CreateVoucherPayload,
  ReplaceVoucherScopesPayload,
  ReplaceVoucherScopesResponse,
  UpdateVoucherPayload,
  Voucher,
  VoucherDeleteResponse,
  VoucherRevokeResponse,
} from '@/client/types/voucher';
import { voucherKeys } from './voucher.query';

const BASE = '/api/admin/v2/vouchers';

const invalidateVoucher = (queryClient: ReturnType<typeof useQueryClient>, vid?: number) => {
  queryClient.invalidateQueries({ queryKey: voucherKeys.lists() });
  queryClient.invalidateQueries({ queryKey: voucherKeys.all });
  if (vid != null) {
    queryClient.invalidateQueries({ queryKey: voucherKeys.detail(vid) });
    queryClient.invalidateQueries({ queryKey: voucherKeys.usage(vid) });
    queryClient.invalidateQueries({ queryKey: voucherKeys.scopes(vid) });
  }
};

// ─── Create ───────────────────────────────────────────────────────────────────

const useCreateVoucherFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (payload: CreateVoucherPayload): Promise<Voucher> => {
    return request<Voucher>(BASE, 'POST', payload);
  }, 'Failed to create voucher');
};

export const useCreateVoucherMutation = () => {
  const queryClient = useQueryClient();
  const createFn = useCreateVoucherFn();
  return useMutation({
    mutationFn: (payload: CreateVoucherPayload) => createFn(payload),
    onSuccess: () => invalidateVoucher(queryClient),
  });
};

// ─── Update (PATCH) ───────────────────────────────────────────────────────────

const useUpdateVoucherFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (vid: number, payload: UpdateVoucherPayload): Promise<Voucher> => {
      return request<Voucher>(`${BASE}/${vid}`, 'PATCH', payload);
    },
    'Failed to update voucher',
  );
};

export const useUpdateVoucherMutation = () => {
  const queryClient = useQueryClient();
  const updateFn = useUpdateVoucherFn();
  return useMutation({
    mutationFn: ({ vid, payload }: { vid: number; payload: UpdateVoucherPayload }) =>
      updateFn(vid, payload),
    onSuccess: (_data, { vid }) => invalidateVoucher(queryClient, vid),
  });
};

// ─── Revoke ───────────────────────────────────────────────────────────────────

const useRevokeVoucherFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (vid: number): Promise<VoucherRevokeResponse> => {
    return request<VoucherRevokeResponse>(`${BASE}/${vid}/revoke`, 'POST', {});
  }, 'Failed to revoke voucher');
};

export const useRevokeVoucherMutation = () => {
  const queryClient = useQueryClient();
  const revokeFn = useRevokeVoucherFn();
  return useMutation({
    mutationFn: (vid: number) => revokeFn(vid),
    onSuccess: (_data, vid) => invalidateVoucher(queryClient, vid),
  });
};

// ─── Expire (soft deactivate) ─────────────────────────────────────────────────

const useExpireVoucherFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (vid: number): Promise<Voucher> => {
    return request<Voucher>(`${BASE}/${vid}/expire`, 'POST', {});
  }, 'Failed to expire voucher');
};

export const useExpireVoucherMutation = () => {
  const queryClient = useQueryClient();
  const expireFn = useExpireVoucherFn();
  return useMutation({
    mutationFn: (vid: number) => expireFn(vid),
    onSuccess: (_data, vid) => invalidateVoucher(queryClient, vid),
  });
};

// ─── Delete ───────────────────────────────────────────────────────────────────

const useDeleteVoucherFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (vid: number): Promise<VoucherDeleteResponse> => {
    return request<VoucherDeleteResponse>(`${BASE}/${vid}`, 'DELETE', {});
  }, 'Failed to delete voucher');
};

export const useDeleteVoucherMutation = () => {
  const queryClient = useQueryClient();
  const deleteFn = useDeleteVoucherFn();
  return useMutation({
    mutationFn: (vid: number) => deleteFn(vid),
    onSuccess: (_data, vid) => invalidateVoucher(queryClient, vid),
  });
};

// ─── Replace scopes ───────────────────────────────────────────────────────────

const useReplaceVoucherScopesFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (vid: number, payload: ReplaceVoucherScopesPayload): Promise<ReplaceVoucherScopesResponse> => {
      return request<ReplaceVoucherScopesResponse>(`${BASE}/${vid}/scopes`, 'PUT', payload);
    },
    'Failed to update voucher scopes',
  );
};

export const useReplaceVoucherScopesMutation = () => {
  const queryClient = useQueryClient();
  const replaceFn = useReplaceVoucherScopesFn();
  return useMutation({
    mutationFn: ({ vid, payload }: { vid: number; payload: ReplaceVoucherScopesPayload }) =>
      replaceFn(vid, payload),
    onSuccess: (_data, { vid }) => invalidateVoucher(queryClient, vid),
  });
};
